import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { delphiService, type GenerationSession } from '../../services/delphiApi';
import useDebounce from '../../hooks/useDebounce';

const DelphiSessionsList: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<GenerationSession[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [contentTypeFilter, setContentTypeFilter] = useState<string>('all');
  const [deletingSessionId, setDeletingSessionId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Filters go back to page 1 - staying on, say, page 5 of an unfiltered
  // list while a filter narrows the results down to 2 pages would show an
  // empty grid.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, statusFilter, contentTypeFilter]);

  useEffect(() => {
    fetchSessions();
  }, [debouncedSearchTerm, statusFilter, contentTypeFilter, page, pageSize]);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: page + 1,
        page_size: pageSize,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (contentTypeFilter !== 'all') {
        const [appLabel, model] = contentTypeFilter.split('.');
        params.app_label = appLabel;
        params.model = model;
      }

      const response = await delphiService.listSessions(params);
      setSessions(response.results);
      setTotal(response.count);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to load sessions',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewSession = (sessionId: number) => {
    navigate(`/delphi/${sessionId}`);
  };

  const handleCreateNew = () => {
    navigate('/delphi/new');
  };

  const handleDeleteSession = async (sessionId: number, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click from triggering
    
    if (!window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
      return;
    }

    setDeletingSessionId(sessionId);
    try {
      await delphiService.deleteSession(sessionId);
      setSessions(sessions.filter((s) => s.id !== sessionId));
      setTotal((t) => Math.max(0, t - 1));
      setSnackbar({
        open: true,
        message: 'Session deleted successfully',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete session',
        severity: 'error',
      });
    } finally {
      setDeletingSessionId(null);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
      renderCell: (params: GridRenderCellParams<GenerationSession>) => (
        <Typography variant="body2" fontWeight="medium">
          #{params.value}
        </Typography>
      ),
    },
    {
      field: 'content_object',
      headerName: 'Content',
      width: 350,
      renderCell: (params: GridRenderCellParams<GenerationSession>) => {
        const contentObj = params.row.content_object;
        if (contentObj?.display_name) {
          return (
            <Box>
              <Typography variant="body2" fontWeight="medium" noWrap>
                {contentObj.display_name}
              </Typography>
              {contentObj.series && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  Series: {contentObj.series}
                </Typography>
              )}
              {contentObj.tournament && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  Tournament: {contentObj.tournament}
                </Typography>
              )}
            </Box>
          );
        }
        return (
          <Typography variant="body2" color="text.secondary">
            {params.row.content_type_name} #{params.row.object_id}
          </Typography>
        );
      },
    },
    {
      field: 'content_type_name',
      headerName: 'Type',
      width: 150,
      renderCell: (params: GridRenderCellParams<GenerationSession>) => (
        <Chip
          label={params.value?.split('.')[1] || 'N/A'}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams<GenerationSession>) => {
        const status = params.value as string;
        const colorMap: Record<string, 'success' | 'warning' | 'default'> = {
          completed: 'success',
          active: 'warning',
          archived: 'default',
        };
        return (
          <Chip
            label={status.charAt(0).toUpperCase() + status.slice(1)}
            size="small"
            color={colorMap[status] || 'default'}
          />
        );
      },
    },
    {
      field: 'iteration_count',
      headerName: 'Iterations',
      width: 100,
      renderCell: (params: GridRenderCellParams<GenerationSession>) => (
        <Typography variant="body2">
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: 'latest_iteration',
      headerName: 'Latest Version',
      width: 130,
      renderCell: (params: GridRenderCellParams<GenerationSession>) => {
        const latest = params.value as { version?: number } | null;
        return (
          <Typography variant="body2" color="text.secondary">
            {latest?.version ? `v${latest.version}` : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 180,
      renderCell: (params: GridRenderCellParams<GenerationSession>) => (
        <Typography variant="body2">
          {new Date(params.value as string).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GenerationSession>) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View Session">
            <IconButton
              size="small"
              onClick={() => handleViewSession(params.row.id)}
              color="primary"
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete Session">
            <IconButton
              size="small"
              onClick={(e) => handleDeleteSession(params.row.id, e)}
              color="error"
              disabled={deletingSessionId === params.row.id}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Delphi Content Sessions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse and manage AI content generation sessions
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchSessions}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
          >
            New Session
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search sessions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Content Type</InputLabel>
            <Select
              value={contentTypeFilter}
              label="Content Type"
              onChange={(e) => setContentTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="cricket.match">Cricket Match</MenuItem>
              <MenuItem value="football.match">Football Match</MenuItem>
            </Select>
          </FormControl>
          {(statusFilter !== 'all' || contentTypeFilter !== 'all' || searchTerm) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setStatusFilter('all');
                setContentTypeFilter('all');
                setSearchTerm('');
              }}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={sessions}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          onRowClick={(params) => handleViewSession(params.row.id)}
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-cell': {
              py: 1,
            },
          }}
          density="compact"
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DelphiSessionsList;

